import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import {
  DragDropContext,
  Droppable as DroppableOrig,
  Draggable as DraggableOrig,
  DropResult,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
// Type cast to avoid React 18.3 / @hello-pangea/dnd render-prop incompatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Droppable = DroppableOrig as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Draggable = DraggableOrig as any;
import { useGetPlannableOrders, useUpdateOrderPlanning, useGetMachines, useGetAllMachines, useCheckMaterialAvailability, useLogMaterialShortageOverride, useSyncDespachado } from '../../hooks/useProductionPlanning';
import type { ProductionOrder, PlanningColumn, MaterialAvailabilityResult } from '../../types/production.types';
import MachineSelectionModal from '../../components/MachineSelectionModal';
import ProcessMachineAssignModal from '../../components/ProcessMachineAssignModal';
import { getMexicoDateString, addDaysToDateString } from '../../lib/mexicoDate';
import MaterialAvailabilityModal from '../../components/MaterialAvailabilityModal';
import GanttView from '../../components/GanttView';
import { useTimeAlerts } from '../../hooks/useTimeAlerts';
import { useAuth } from '../../hooks/useAuth';

interface WeightProgress {
  totalWeightKg: number;
  byProcess: Array<{ processName: string; weightKg: number }>;
}

interface OptimizationResult {
  optimized_schedule: Array<{
    order_id: string;
    assigned_machine: string;
    recommended_start: string;
    recommended_end: string;
    sequence: number;
  }>;
  analysis: {
    total_setup_time_saved_minutes: number;
    workload_balance_score: number | null;
    on_time_delivery_rate: number | null; // null when no scheduled order has a due date
    data_quality?: { orders: number; fallbackOrders: number; assumedOrders: number; confidence: 'HIGH' | 'MEDIUM' | 'LOW' };
  };
  recommendations: string[];
  warnings: string[];
}

type ViewMode = 'kanban' | 'gantt';

// Componente de tarjeta individual — compacta por defecto, expandible al hacer clic
const OrderCard: React.FC<{
  order: ProductionOrder;
  onSchedule?: (order: ProductionOrder) => void;
  onViewMaterials?: (order: ProductionOrder) => void;
  weightProgress?: WeightProgress;
  timeAlert?: { estimatedFormatted: string; elapsedFormatted: string; progressPct: number; isExceeded: boolean; overageFormatted: string | null } | null;
}> = ({ order, onSchedule, onViewMaterials, weightProgress, timeAlert }) => {
  const [expanded, setExpanded] = useState(false);
  // ROT-209: per-process machine assignment modal, opened from this card.
  const [showMachineAssign, setShowMachineAssign] = useState(false);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case '1': return 'bg-red-500';
      case '2': return 'bg-yellow-400';
      case '3': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case '1': return 'Alta';
      case '2': return 'Media';
      case '3': return 'Baja';
      default: return priority;
    }
  };

  const getDateAlert = (dueDate: string | null | undefined) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { color: 'text-red-600 font-bold', icon: '🔴', label: 'Vencida' };
    if (diffDays <= 3) return { color: 'text-orange-500', icon: '⚠️', label: 'Urgente' };
    return null;
  };

  const getDaysToProduction = (plannedDate: string | null | undefined) => {
    if (!plannedDate) return null;
    const todayStr = getMexicoDateString();
    if (plannedDate < todayStr) {
      const diff = Math.ceil((new Date(todayStr).getTime() - new Date(plannedDate).getTime()) / 86400000);
      return { label: `${diff}d atraso`, color: 'text-red-600 bg-red-50' };
    } else if (plannedDate === todayStr) {
      return { label: 'hoy', color: 'text-blue-600 bg-blue-50' };
    } else {
      const diff = Math.ceil((new Date(plannedDate).getTime() - new Date(todayStr).getTime()) / 86400000);
      return diff === 1
        ? { label: 'mañana', color: 'text-green-600 bg-green-50' }
        : { label: `en ${diff}d`, color: 'text-gray-600 bg-gray-50' };
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const dateAlert = getDateAlert(order.dueDate);
  const daysToProduction = getDaysToProduction(order.plannedStartDate);
  const planned = Number(order.quantityPlanned) || 0;
  const registered = weightProgress?.totalWeightKg ?? 0;
  const weightPct = planned > 0 ? Math.min(100, (registered / planned) * 100) : 0;
  const barColor = weightPct >= 100 ? 'bg-green-500' : weightPct >= 60 ? 'bg-blue-500' : weightPct > 0 ? 'bg-amber-400' : 'bg-gray-200';

  return (
    <div
      className={`bg-white border rounded-md shadow-sm mb-1 transition-all duration-200 cursor-pointer select-none
        ${timeAlert?.isExceeded ? 'border-red-400' : 'border-gray-200'}
        ${expanded ? 'shadow-md' : 'hover:border-gray-400'}`}
      onClick={() => setExpanded(e => !e)}
    >
      {/* ── COMPACT ROW (always visible) ── */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 min-w-0">
        {/* Priority dot */}
        <div
          className={`flex-shrink-0 w-2 h-2 rounded-full ${getPriorityColor(order.priority)}`}
          title={`Prioridad ${getPriorityLabel(order.priority)}`}
        />
        {/* Time-exceeded indicator */}
        {timeAlert?.isExceeded && (
          <span className="flex-shrink-0 text-red-500 text-xs animate-pulse" title="Tiempo excedido">⏰</span>
        )}
        {/* Order number */}
        <span className="flex-shrink-0 font-semibold text-xs text-gray-800">{order.orderNumber}</span>
        {/* Customer – truncated */}
        <span className="flex-1 text-xs text-gray-500 truncate min-w-0">{order.customerName}</span>
        {/* Qty */}
        <span className="flex-shrink-0 text-xs text-gray-700 font-medium whitespace-nowrap">
          {Number(order.quantityPlanned).toLocaleString('es-MX')} {order.unit}
        </span>
        {/* Days-to-production badge */}
        {daysToProduction && (
          <span className={`flex-shrink-0 text-xs px-1 py-0.5 rounded font-medium ${daysToProduction.color}`}>
            {daysToProduction.label}
          </span>
        )}
        {/* Due-date alert */}
        {dateAlert && <span className="flex-shrink-0 text-xs">{dateAlert.icon}</span>}
        {/* ROT-210: order running on 2+ machines at once right now (e.g. printing
            and laminating simultaneously) */}
        {order.activeMachines && order.activeMachines.length >= 2 && (
          <span
            className="flex-shrink-0 text-xs"
            title={`Simultánea:\n${order.activeMachines.map(m => `${m.process} → ${m.machineName}`).join('\n')}`}
          >
            ⚡
          </span>
        )}
        {/* Expand chevron */}
        <span className="flex-shrink-0 text-gray-400 text-xs ml-0.5">{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Weight mini-bar (always visible when there's progress) */}
      {planned > 0 && weightPct > 0 && !expanded && (
        <div className="mx-2 mb-1">
          <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
            <div className={`h-1 rounded-full ${barColor}`} style={{ width: `${weightPct}%` }} />
          </div>
        </div>
      )}

      {/* ── EXPANDED PANEL ── */}
      {expanded && (
        <div className="border-t border-gray-100 px-3 py-2 space-y-2" onClick={e => e.stopPropagation()}>
          {/* Product */}
          <div>
            <p className="text-xs font-semibold text-gray-900 leading-tight" title={order.productName}>{order.productName}</p>
            <p className="text-xs text-gray-400">{order.productCode}</p>
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap gap-1">
            {order.externalKey ? (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700 border border-purple-200">↓ Imp.</span>
            ) : (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-500 border border-gray-200">Manual</span>
            )}
            {order.lotCode && (
              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{order.lotCode}</span>
            )}
            {daysToProduction && (
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${daysToProduction.color}`}>
                📅 {daysToProduction.label}
              </span>
            )}
          </div>

          {/* Quantity */}
          <p className="text-xs text-gray-700">
            <span className="font-medium">Cantidad:</span> {Number(order.quantityPlanned).toFixed(2)} {order.unit}
          </p>

          {/* Weight progress */}
          {planned > 0 && (
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-gray-500 font-medium">Avance kg</span>
                <span className={`text-xs font-semibold ${weightPct >= 100 ? 'text-green-600' : weightPct > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                  {registered.toFixed(1)} / {planned.toFixed(1)} ({weightPct.toFixed(0)}%)
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className={`h-1.5 rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${weightPct}%` }} />
              </div>
            </div>
          )}

          {/* Dates */}
          {order.dueDate && (
            <p className={`text-xs ${dateAlert?.color || 'text-gray-600'}`}>
              {dateAlert?.icon && <span className="mr-1">{dateAlert.icon}</span>}
              <span className="font-medium">Entrega:</span> {formatDate(order.dueDate)}
              {dateAlert?.label && <span className="ml-1 font-bold">({dateAlert.label})</span>}
            </p>
          )}
          {order.plannedStartDate && (
            <p className="text-xs text-gray-600">
              <span className="font-medium">Planeada:</span> {formatDate(order.plannedStartDate)}
            </p>
          )}

          {/* Machine */}
          {order.machineName && (
            <p className="text-xs text-blue-600 font-medium truncate">📍 {order.machineName}</p>
          )}

          {/* ROT-210: which processes are simultaneously active on which machines */}
          {order.activeMachines && order.activeMachines.length >= 2 && (
            <div className="rounded-md px-2 py-1.5 bg-purple-50 border border-purple-200">
              <p className="text-xs font-semibold text-purple-700 flex items-center gap-1">
                <span>⚡</span> Ejecutándose simultáneamente
              </p>
              {order.activeMachines.map((m, i) => (
                <p key={i} className="text-xs text-purple-600">{m.process} → {m.machineName}</p>
              ))}
            </div>
          )}

          {/* ROT-218: static per-process machine assignment, shown even when
              only one step differs from the order's own machine (unlike the
              ⚡ badge above, which needs 2+ simultaneous steps). */}
          {order.processMachineAssignments && order.processMachineAssignments.length > 0 && (
            <div className="rounded-md px-2 py-1.5 bg-blue-50 border border-blue-200">
              <p className="text-xs font-semibold text-blue-700">Máquina por proceso</p>
              {order.processMachineAssignments.map((m, i) => (
                <p key={i} className="text-xs text-blue-600">{m.process} → {m.machineName}</p>
              ))}
            </div>
          )}

          {/* ROT-209: assign a machine per process, right from the board —
              previously only possible from the order's edit form. */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowMachineAssign(true); }}
            className="w-full py-1 px-2 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded transition-colors flex items-center justify-center gap-1"
          >
            🔀 Máquinas por Proceso
          </button>
          {showMachineAssign && (
            <ProcessMachineAssignModal
              orderId={order.id}
              orderNumber={order.orderNumber}
              onClose={() => setShowMachineAssign(false)}
            />
          )}

          {/* Time alert */}
          {timeAlert && (
            <div className={`rounded-md px-2 py-1.5 ${timeAlert.isExceeded ? 'bg-red-50 border border-red-300' : 'bg-gray-50 border border-gray-200'}`}>
              {timeAlert.isExceeded ? (
                <>
                  <div className="flex items-center gap-1 text-red-700 font-semibold text-xs">
                    <span className="animate-pulse">⏰</span> Tiempo excedido por {timeAlert.overageFormatted}
                  </div>
                  <div className="flex gap-3 text-xs text-red-600 mt-0.5">
                    <span>Est: {timeAlert.estimatedFormatted}</span>
                    <span>Trans: {timeAlert.elapsedFormatted}</span>
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-1 mt-1">
                    <div className="bg-red-500 h-1 rounded-full w-full" />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>⏱ Tiempo</span>
                    <span>{timeAlert.elapsedFormatted} / {timeAlert.estimatedFormatted}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                    <div className="bg-blue-400 h-1 rounded-full transition-all" style={{ width: `${timeAlert.progressPct}%` }} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Schedule button */}
          {onSchedule && (
            <button
              onClick={(e) => { e.stopPropagation(); onSchedule(order); }}
              className="w-full py-1 px-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors flex items-center justify-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Programar
            </button>
          )}

          {/* View materials availability button — only for orders linked to Comercia */}
          {onViewMaterials && order.externalKey && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewMaterials(order); }}
              className="w-full py-1 px-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded transition-colors flex items-center justify-center gap-1"
            >
              📦 Ver materiales
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface AiContextValues { objetivo: string; plan: string; reglas: string }

// Keeps the three textareas' state local: typing used to re-render the whole
// planning board on every keystroke, which made the rules box crawl.
const AiContextForm: React.FC<{
  initial: AiContextValues;
  error: string | null;
  onCancel: () => void;
  onSubmit: (values: AiContextValues) => void;
}> = React.memo(({ initial, error, onCancel, onSubmit }) => {
  const [values, setValues] = useState(initial);
  const field = (key: keyof AiContextValues) => ({
    value: values[key],
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setValues(v => ({ ...v, [key]: e.target.value })),
  });
  const textareaClass = 'w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none';
  return (
    <div className="p-6 space-y-5">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          🎯 Objetivo de la optimización
        </label>
        <textarea {...field('objetivo')} rows={3} className={textareaClass}
          placeholder="Ej. Minimizar tiempos de cambio y maximizar entrega a tiempo..." />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          📋 Plan actual / Contexto adicional
        </label>
        <textarea {...field('plan')} rows={3} className={textareaClass}
          placeholder="Ej. La máquina M1 tiene mantenimiento el lunes, hay 3 pedidos urgentes de cliente ABC..." />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          📐 Reglas adicionales para el agente
        </label>
        <textarea {...field('reglas')} rows={3} className={textareaClass}
          placeholder="Ej. No mover órdenes en producción, priorizar máquinas de alta velocidad, agrupar por cliente..." />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={onCancel}
          className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all"
        >
          Cancelar
        </button>
        <button
          onClick={() => onSubmit(values)}
          className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
        >
          Continuar → Optimizar
        </button>
      </div>
    </div>
  );
});

const PlanningBoardView: React.FC = () => {
  const { user } = useAuth();
  const { data: orders, isLoading, error, refetch, isFetching } = useGetPlannableOrders();
  const machinesFromOrders = useGetMachines();
  const { data: machinesFromDB } = useGetAllMachines();
  const updateOrderMutation = useUpdateOrderPlanning();

  // Estado para ordenamiento de máquinas en Kanban (declarado aquí para usarlo en useMemo)
  const [machineSortDir, setMachineSortDir] = useState<'asc' | 'desc'>('asc');
  
  // Combinar máquinas de órdenes y de la base de datos, ordenadas por Código de catálogo
  const machines = useMemo(() => {
    const sortedDB = [...(machinesFromDB || [])].sort((a, b) => {
      const cmp = a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });
      return machineSortDir === 'asc' ? cmp : -cmp;
    });
    const dbMachineNames = sortedDB.map(m => m.name);
    // Normalizar a mayúsculas y sin espacios para la comparación, evita duplicados
    // por diferencia de capitalización o espacios extra en production_orders.machineName
    const dbNormalized = new Set([
      ...sortedDB.map(m => m.name.trim().toUpperCase()),
      ...sortedDB.map(m => m.code.trim().toUpperCase()),
    ]);
    // Equipment identity migration Phase 4g: a raw machineName only earns its
    // own "extra" swimlane if some order carrying it is a genuine orphan (no
    // equipmentId that resolves against the catalog). A name used exclusively
    // by orders that already resolve is stale cached text from before a
    // catalog rename — listing it here would render a second, permanently-
    // empty swimlane that's still a live drop target able to mint new orphans.
    const resolvedEquipmentIds = new Set((machinesFromDB || []).map(m => m.id));
    const namesWithGenuineOrphan = new Set(
      (orders || [])
        .filter(o => o.machineName && !(o.equipmentId != null && resolvedEquipmentIds.has(o.equipmentId)))
        .map(o => o.machineName as string)
    );
    const extraFromOrders = machinesFromOrders
      .filter(name => !dbNormalized.has(name.trim().toUpperCase()) && namesWithGenuineOrphan.has(name))
      .sort();
    return [...dbMachineNames, ...extraFromOrders];
  }, [machinesFromOrders, machinesFromDB, machineSortDir, orders]);

  // Mapa de nombre → código para mostrar el código en los encabezados
  const machineCodeMap = useMemo(() => {
    const map: Record<string, string> = {};
    (machinesFromDB || []).forEach(m => { map[m.name] = m.code; });
    return map;
  }, [machinesFromDB]);

  // Mapa de código → nombre para mostrar el nombre en resultados de IA
  const machineCodeToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    (machinesFromDB || []).forEach(m => { map[m.code] = m.name; });
    return map;
  }, [machinesFromDB]);
  
  // Time alerts
  const { data: timeAlertsData } = useTimeAlerts();
  const timeAlertMap = useMemo(() => {
    const map: Record<string, { estimatedFormatted: string; elapsedFormatted: string; progressPct: number; isExceeded: boolean; overageFormatted: string | null }> = {};
    (timeAlertsData?.alerts || []).forEach(a => { map[String(a.orderId)] = a; });
    // Also flag orders that have any exceeded process
    (timeAlertsData?.processAlerts || []).filter(p => p.isExceeded).forEach(p => {
      if (!map[String(p.orderId)]) {
        map[String(p.orderId)] = {
          estimatedFormatted: p.estimatedFormatted,
          elapsedFormatted: p.elapsedFormatted,
          progressPct: p.progressPct,
          isExceeded: true,
          overageFormatted: p.overageFormatted,
        };
      } else {
        map[String(p.orderId)].isExceeded = true;
      }
    });
    return map;
  }, [timeAlertsData]);

  // Weight progress state
  const [weightProgressMap, setWeightProgressMap] = useState<Record<string, WeightProgress>>({});

  // Fetch weight progress whenever orders change
  useEffect(() => {
    if (!orders?.length) return;
    const ids = orders.map((o: ProductionOrder) => o.id).join(',');
    const token = localStorage.getItem('token');
    fetch(`/api/production-orders/weight-progress?ids=${ids}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then((data: Array<{ orderId: number; totalWeightKg: number; byProcess: any[] }>) => {
        const map: Record<string, WeightProgress> = {};
        data.forEach(d => {
          map[String(d.orderId)] = { totalWeightKg: d.totalWeightKg, byProcess: d.byProcess };
        });
        setWeightProgressMap(map);
      })
      .catch(() => {});
  }, [orders]);

  // Estado para el modal de selección de máquina
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingDragOperation, setPendingDragOperation] = useState<{
    orderId: string;
    targetColumn: string;
    // ROT-203: the machine swimlane the order was dropped onto, if any —
    // pre-fills MachineSelectionModal instead of leaving it blank and
    // making the user re-pick a machine they already told us via the drop.
    machineName?: string | null;
  } | null>(null);
  const [editingOrder, setEditingOrder] = useState<ProductionOrder | null>(null);

  // Estado para el modal de disponibilidad de materiales
  const checkMaterialAvailability = useCheckMaterialAvailability();
  const logMaterialShortageOverride = useLogMaterialShortageOverride();
  const syncDespachado = useSyncDespachado();
  const [materialViewer, setMaterialViewer] = useState<{
    mode: 'gate' | 'readonly';
    order: ProductionOrder;
    result: MaterialAvailabilityResult | null;
    loading: boolean;
  } | null>(null);
  const [overrideNote, setOverrideNote] = useState('');

  // Payload del movimiento pendiente de confirmación (se ejecuta tras confirmar en el modal)
  const [pendingMove, setPendingMove] = useState<{
    orderId: string;
    payload: any;
    despachado: boolean;
  } | null>(null);

  // Abre el modal de materiales y guarda el movimiento pendiente.
  // Si la orden no tiene externalKey, ejecuta el movimiento directo sin mostrar el modal.
  const openMaterialCheckAndMove = async (order: ProductionOrder, payload: any, despachado: boolean) => {
    if (!order.externalKey) {
      // Sin vínculo Comercia: mover directo
      updateOrderMutation.mutate({ orderId: order.id, payload });
      return;
    }
    setPendingMove({ orderId: order.id, payload, despachado });
    setMaterialViewer({ mode: 'gate', order, result: null, loading: true });
    try {
      const result = await checkMaterialAvailability.mutateAsync(order.id);
      setMaterialViewer({ mode: 'gate', order, result, loading: false });
    } catch {
      setMaterialViewer({ mode: 'gate', order, result: { checked: false, items: [] }, loading: false });
    }
  };

  // Botón "📦 Ver materiales" de la tarjeta: sólo muestra el desglose, no mueve nada.
  const handleViewMaterialsClick = (order: ProductionOrder) => {
    setMaterialViewer({ mode: 'readonly', order, result: null, loading: true });
    checkMaterialAvailability.mutateAsync(order.id)
      .then((result) => setMaterialViewer({ mode: 'readonly', order, result, loading: false }))
      .catch(() => setMaterialViewer({ mode: 'readonly', order, result: { checked: false, items: [] }, loading: false }));
  };

  // Confirmar movimiento: ejecuta el move + llama PatchDespachado en Comercia
  const handleConfirmMove = async () => {
    if (!pendingMove) return;
    const { orderId, payload, despachado } = pendingMove;
    const hadShortage = materialViewer?.result?.checked && materialViewer.result.hasShortage;
    const shortageItems = materialViewer?.result?.items || [];
    const note = overrideNote;
    setMaterialViewer(null);
    setPendingMove(null);
    setOverrideNote('');
    updateOrderMutation.mutate({ orderId, payload });
    // Llamar PatchDespachado en segundo plano (no bloqueante)
    syncDespachado.mutate({ orderId, despachado });
    // Registrar en bitácora el override de faltante de material (no bloqueante)
    if (hadShortage) {
      logMaterialShortageOverride.mutate({ orderId, note, items: shortageItems });
    }
  };

  // Estado para filtrar por máquina
  const [selectedMachine, setSelectedMachine] = useState<string | null>(null);
  
  // Estado para el modo de vista (Kanban o Gantt)
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  
  // Estado para contadores del Gantt
  const [ganttCounters, setGanttCounters] = useState<{ backlogCount: number; weekCount: number; todayCount: number } | null>(null);

  // Estado para filtro de equipos (transversal: Kanban + Gantt)
  const [filterMachines, setFilterMachines] = useState<string[]>([]);
  const [showMachineFilter, setShowMachineFilter] = useState(false);

  // Estado para flujo IA (3 pasos)
  const [aiModalStep, setAiModalStep] = useState<0 | 1 | 2 | 3>(0); // 0=cerrado, 1=contexto, 2=procesando, 3=preview
  const [aiContext, setAiContext] = useState({
    objetivo: 'Optimizar la distribución de órdenes en las máquinas para minimizar tiempos de setup y respetar fechas de entrega',
    plan: '',
    reglas: '',
  });
  const [aiOptimization, setAiOptimization] = useState<OptimizationResult | null>(null);
  const [aiSimulationId, setAiSimulationId] = useState<number | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [aiApplying, setAiApplying] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  const handleOpenAI = () => {
    setAiError(null);
    setAiOptimization(null);
    setAiSimulationId(null);
    setAiModalStep(1);
  };

  const closeAiModal = () => {
    setAiModalStep(0);
    setAiSimulationId(null);
  };

  const handleContinueAI = async (values: AiContextValues) => {
    setAiContext(values);
    setAiModalStep(2);
    setAiError(null);
    setAiNotice(null);
    try {
      const response = await fetch('/api/gantt/optimize', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          objetivo: values.objetivo,
          plan: values.plan,
          reglas: values.reglas,
          // Chain iterations: the previous simulation becomes the parent
          ...(aiSimulationId != null ? { parentSimulationId: aiSimulationId } : {}),
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        if (err.code === 'FREE_CLOUD_BUDGET_EXCEEDED') throw new Error('Presupuesto de IA agotado. Por favor actualiza tu plan.');
        throw new Error(err.error || 'Error en la optimización');
      }
      const result = await response.json();
      setAiOptimization(result.optimization);
      setAiSimulationId(typeof result.simulationId === 'number' ? result.simulationId : null);
      setAiModalStep(3);
    } catch (err: any) {
      setAiError(err.message);
      setAiModalStep(1);
    }
  };

  const handleApplyOptimization = async () => {
    if (!aiOptimization) return;
    setAiApplying(true);
    try {
      const response = await fetch('/api/gantt/apply-optimization', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          schedule: aiOptimization.optimized_schedule,
          ...(aiSimulationId != null ? { simulationId: aiSimulationId } : {}),
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Error al aplicar optimización');
      }
      const applied = await response.json().catch(() => ({}));
      if (Array.isArray(applied.skipped) && applied.skipped.length > 0) {
        setAiNotice(`Optimización aplicada (${applied.appliedCount ?? 0} órdenes). Se omitieron ${applied.skipped.length} orden(es) que ya no están autorizadas: ${applied.skipped.join(', ')}`);
      } else {
        setAiNotice(null);
      }
      setAiModalStep(0);
      setAiOptimization(null);
      setAiSimulationId(null);
      refetch();
    } catch (err: any) {
      setAiError(err.message);
    } finally {
      setAiApplying(false);
    }
  };

  const toggleMachineFilter = (machineName: string) => {
    setFilterMachines(prev =>
      prev.includes(machineName)
        ? prev.filter(m => m !== machineName)
        : [...prev, machineName]
    );
  };
  
  // Función para toggle del filtro de máquina
  const handleMachineClick = (machineName: string | null) => {
    if (selectedMachine === machineName) {
      // Si hacemos clic en la misma máquina, quitamos el filtro
      setSelectedMachine(null);
    } else {
      // Filtramos por la máquina seleccionada
      setSelectedMachine(machineName);
    }
  };

  // Función para abrir modal desde el botón "Programar" de la tarjeta
  const handleScheduleClick = (order: ProductionOrder) => {
    setEditingOrder(order);
    setIsModalOpen(true);
  };

  // Merges the "Notas o instrucciones especiales" textarea's text into the
  // order's EXISTING structured notes JSON (as the `observations` key),
  // instead of sending it as the entire notes column — sending it raw
  // overwrote processType/processSpecs/orderType/deliveryDate/etc. for any
  // order this modal touched (ROT-192). If the existing notes are already
  // corrupted (legacy plain text from before this fix), there's nothing
  // structured left to preserve — start fresh with just observations rather
  // than nesting the old plain text inside itself.
  const mergeObservationsIntoNotes = (
    existingNotesRaw: string | null | undefined,
    observationsText: string | undefined
  ): string | undefined => {
    if (observationsText === undefined) return undefined;
    let existingNotes: any = {};
    if (existingNotesRaw) {
      try { existingNotes = JSON.parse(existingNotesRaw); } catch { /* legacy plain-text notes — nothing structured to preserve */ }
    }
    return JSON.stringify({ ...existingNotes, observations: observationsText });
  };

  // Función para manejar la selección de máquina desde el modal
  const handleMachineSelect = (data: { machineName: string; plannedStartDate: string; notes?: string }) => {
    const resolved = resolveMachineAssignment(data.machineName);
    // Si estamos editando una orden desde el botón
    if (editingOrder) {
      const mergedNotes = mergeObservationsIntoNotes(editingOrder.notes, data.notes);
      updateOrderMutation.mutate({
        orderId: editingOrder.id,
        payload: {
          ...resolved,
          plannedStartDate: data.plannedStartDate,
          // Only include `notes` when there's actually a merged value — an
          // own key set to `undefined` still gets spread into the
          // optimistic-update cache (onMutate does {...order, ...payload}),
          // nulling out order.notes there even though the wire request
          // correctly drops it. If a later refetch fails, that undefined
          // value sticks, and the NEXT schedule action on this order would
          // merge against nothing and lose everything (ROT-192 final review).
          ...(mergedNotes !== undefined && { notes: mergedNotes })
        }
      });
      setEditingOrder(null);
      return;
    }

    // Si estamos arrastrando (Backlog → Planeado)
    if (!pendingDragOperation) return;
    const { orderId } = pendingDragOperation;
    const order = orders?.find((o) => o.id === orderId);
    const mergedNotes = mergeObservationsIntoNotes(order?.notes, data.notes);
    const payload = {
      ...resolved,
      plannedStartDate: data.plannedStartDate,
      // Same reasoning as the editingOrder branch above — omit the key
      // entirely rather than setting it undefined, so the optimistic cache
      // update can't null out order.notes (ROT-192 final review).
      ...(mergedNotes !== undefined && { notes: mergedNotes })
    };
    setPendingDragOperation(null);

    // Buscar la orden para saber si tiene externalKey
    if (order) {
      openMaterialCheckAndMove(order, payload, true);
    } else {
      updateOrderMutation.mutate({ orderId, payload });
    }
  };

  // Función para determinar la columna de una orden
  const getOrderColumn = (order: ProductionOrder): PlanningColumn => {
    // En Producción: tiene fecha de inicio real pero no ha terminado
    if (order.actualStartDate && !order.actualEndDate) {
      return 'IN_PRODUCTION';
    }

    // Hoy: tiene máquina Y plannedStartDate es hoy Y no ha iniciado
    const today = getMexicoDateString();
    if (order.machineName && order.plannedStartDate === today && !order.actualStartDate) {
      return 'TODAY';
    }

    // Planeado: tiene máquina Y tiene fecha planeada (que no sea hoy) Y no ha iniciado
    if (order.machineName && order.plannedStartDate && order.plannedStartDate !== today && !order.actualStartDate) {
      return 'PLANNED';
    }

    // Backlog: sin máquina O (tiene máquina pero sin fecha planeada)
    return 'BACKLOG';
  };

  const groupedOrders = useMemo(() => {
    if (!orders) return [];

    const swimlanes = ['SIN ASIGNAR', ...machines];
    const columns: PlanningColumn[] = ['BACKLOG', 'PLANNED', 'TODAY', 'IN_PRODUCTION'];

    // filterMachines tiene prioridad; si está vacío y selectedMachine activo, filtra por ese
    let filteredSwimlanes: string[];
    if (filterMachines.length > 0) {
      filteredSwimlanes = swimlanes.filter(m => m === 'SIN ASIGNAR' || filterMachines.includes(m));
    } else if (selectedMachine !== null) {
      filteredSwimlanes = [selectedMachine === null ? 'SIN ASIGNAR' : selectedMachine];
    } else {
      filteredSwimlanes = swimlanes;
    }

    // Equipment identity migration Phase 4g: resolve an order's swimlane key
    // via its equipmentId first, against the SAME catalog (machinesFromDB)
    // the swimlane headers themselves are built from — so a machine renamed
    // in the catalog can't strand existing orders in a stale-named "extra"
    // swimlane the way plain text comparison would. Falls back to the
    // existing trim+uppercase machineName comparison only when equipmentId
    // is absent or doesn't resolve (deleted/deactivated equipment, or a
    // genuinely orphaned legacy row) — unchanged for that case.
    const machineNameById = new Map(
      (machinesFromDB || []).map(m => [m.id, m.name.trim().toUpperCase()])
    );
    const resolveOrderMachineKey = (order: ProductionOrder): string | null => {
      if (order.equipmentId != null) {
        const catalogKey = machineNameById.get(order.equipmentId);
        if (catalogKey) return catalogKey;
      }
      return order.machineName ? order.machineName.trim().toUpperCase() : null;
    };

    return filteredSwimlanes.map((machine) => ({
      machineName: machine === 'SIN ASIGNAR' ? null : machine,
      columns: columns.map((column) => ({
        column,
        orders: orders.filter((order) => {
          const orderColumn = getOrderColumn(order);
          const orderMachine = resolveOrderMachineKey(order);
          const targetMachine = machine === 'SIN ASIGNAR' ? null : machine.trim().toUpperCase();

          return orderColumn === column && orderMachine === targetMachine;
        }),
      })),
    }));
  }, [orders, machines, selectedMachine, machinesFromDB]);

  // Equipment identity migration Phase 3: matches a raw machine-name string
  // (parsed from the drag target) against the catalog already loaded for
  // this board, mirroring matchEquipmentId's trim+lowercase, code-or-name
  // semantics. Returns the catalog's CURRENT name for the match — never the
  // raw input text — so a resolved assignment can't introduce a new
  // spelling variant. On no match, keeps the raw text (orphan case, same as
  // before this phase) with a null equipmentId.
  const resolveMachineAssignment = (
    machine: string | null
  ): { machineName: string | null; equipmentId?: number | null } => {
    if (!machine) return { machineName: null, equipmentId: null };
    // If the catalog hasn't loaded yet, don't claim a definitive "no match" —
    // omit equipmentId entirely so the backend falls back to its own
    // text-resolve instead of us silently manufacturing an orphan row.
    if (!machinesFromDB) return { machineName: machine };
    const trimmed = machine.trim().toLowerCase();
    const match = machinesFromDB.find(
      (m) => m.code?.toLowerCase() === trimmed || m.name?.toLowerCase() === trimmed
    );
    return match
      ? { machineName: match.name, equipmentId: match.id }
      : { machineName: machine, equipmentId: null };
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // Validar destino válido
    if (!destination) return;

    // Validar que cambió de posición
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const order = orders?.find((o) => o.id === draggableId);
    if (!order) return;

    const [targetColumn, targetMachine] = destination.droppableId.split('-');
    const machine = targetMachine === 'SIN_ASIGNAR' ? null : targetMachine.replace(/_/g, ' ');

    const currentColumn = getOrderColumn(order);
    const today = getMexicoDateString();

    // Caso 1: Backlog -> Planeado (primero seleccionar máquina, luego verificar materiales)
    // ROT-203: pass along the machine swimlane the order was dropped onto —
    // dropping it straight into e.g. FLEXO 3's Planeado column already told
    // us the machine, so the modal should open pre-filled with it instead
    // of blank.
    if (currentColumn === 'BACKLOG' && targetColumn === 'PLANNED') {
      setPendingDragOperation({ orderId: order.id, targetColumn: 'PLANNED', machineName: machine });
      setIsModalOpen(true);
      return;
    }
    // Caso 2: Planeado -> Hoy
    else if (currentColumn === 'PLANNED' && targetColumn === 'TODAY') {
      openMaterialCheckAndMove(order, { plannedStartDate: today }, true);
      return;
    }
    // Caso 3: Hoy -> En Producción
    else if (currentColumn === 'TODAY' && targetColumn === 'IN_PRODUCTION') {
      openMaterialCheckAndMove(order, { actualStartDate: new Date().toISOString() }, true);
      return;
    }
    // Caso 4: Hoy -> Planeado (devolver a planeado)
    else if (currentColumn === 'TODAY' && targetColumn === 'PLANNED') {
      openMaterialCheckAndMove(order, { plannedStartDate: null }, true);
      return;
    }
    // Caso 5: Cambiar máquina en Planeado
    else if (currentColumn === 'PLANNED' && targetColumn === 'PLANNED' && order.machineName !== machine) {
      openMaterialCheckAndMove(order, resolveMachineAssignment(machine), true);
      return;
    }
    // Caso 6: Cambiar máquina en Hoy
    else if (currentColumn === 'TODAY' && targetColumn === 'TODAY' && order.machineName !== machine) {
      openMaterialCheckAndMove(order, { ...resolveMachineAssignment(machine), plannedStartDate: today }, true);
      return;
    }
    // Caso 7: Planeado -> Backlog
    else if (currentColumn === 'PLANNED' && targetColumn === 'BACKLOG') {
      openMaterialCheckAndMove(order, { ...resolveMachineAssignment(machine), plannedStartDate: null }, false);
      return;
    }
    // Caso 8: Hoy -> Backlog
    else if (currentColumn === 'TODAY' && targetColumn === 'BACKLOG') {
      openMaterialCheckAndMove(order, { ...resolveMachineAssignment(machine), plannedStartDate: null }, false);
      return;
    }
    // Caso 9: Backlog -> Backlog (solo reasignación de máquina, sin verificación)
    else if (currentColumn === 'BACKLOG' && targetColumn === 'BACKLOG') {
      if (order.machineName === machine) return;
      updateOrderMutation.mutate({ orderId: order.id, payload: resolveMachineAssignment(machine) });
      return;
    } else {
      return;
    }
  };

  // ROT-021: orders:planning-board decouples this menu item/page from
  // orders:list (previously shared with Lista Maestra and Órdenes de
  // Producción, so none of the three could be shown/hidden independently).
  // There's no route guard anywhere in the app — AppLayout.tsx only hides
  // the nav link — so this check is what actually blocks direct navigation
  // to the URL for a role that doesn't have it.
  if (!user?.permissions?.includes('orders:planning-board')) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold mb-2">Sin acceso</p>
          <p>No tienes permiso para ver el Tablero de Planeación.</p>
          <p className="mt-1 text-xs">El permiso <code>orders:planning-board</code> no está asignado a tu rol. Pide al administrador que lo asigne en Roles y Permisos, luego vuelve a iniciar sesión.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tablero de planeación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold mb-2">Error al cargar órdenes</p>
          <p>{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const columnHeaders = [
    { id: 'BACKLOG', label: 'Backlog', description: 'Sin Asignar' },
    { id: 'PLANNED', label: 'Planeado', description: 'Semanal' },
    { id: 'TODAY', label: 'Hoy', description: 'Plan Diario' },
    { id: 'IN_PRODUCTION', label: 'En Producción', description: 'En Proceso' },
  ];

  // Calcular indicadores clave
  const today = getMexicoDateString();
  const weekFromNowStr = addDaysToDateString(today, 7);

  // Use Gantt counters when in Gantt mode, otherwise use Kanban data
  const kanbanBacklogCount = orders?.filter(order => getOrderColumn(order) === 'BACKLOG').length || 0;
  const kanbanTodayCount = orders?.filter(order => order.plannedStartDate === today).length || 0;
  const kanbanWeekCount = orders?.filter(order => {
    if (!order.plannedStartDate) return false;
    return order.plannedStartDate >= today && order.plannedStartDate <= weekFromNowStr;
  }).length || 0;
  
  const backlogCount = viewMode === 'gantt' && ganttCounters ? ganttCounters.backlogCount : kanbanBacklogCount;
  const todayCount = viewMode === 'gantt' && ganttCounters ? ganttCounters.todayCount : kanbanTodayCount;
  const weekCount = viewMode === 'gantt' && ganttCounters ? ganttCounters.weekCount : kanbanWeekCount;

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="h-screen flex flex-col bg-gray-50">
        {aiNotice && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-sm text-amber-800 flex items-start justify-between gap-4">
            <span>{aiNotice}</span>
            <button onClick={() => setAiNotice(null)} className="text-amber-700 hover:text-amber-900 leading-none text-lg">×</button>
          </div>
        )}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">📋 Tablero de Planeación</h1>
              <p className="text-sm text-gray-600 mt-1">
                {viewMode === 'kanban' 
                  ? 'Arrastra las órdenes para asignarlas a máquinas y programarlas'
                  : 'Vista de Gantt interactiva con optimización por IA'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Toggle de vista */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                    viewMode === 'kanban'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  Kanban
                </button>
                <button
                  onClick={() => setViewMode('gantt')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                    viewMode === 'gantt'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Gantt
                </button>
              </div>

              {/* Filtro de equipos — transversal */}
              <div className="relative">
                <button
                  onClick={() => setShowMachineFilter(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm border transition-all ${
                    filterMachines.length > 0
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  title="Filtrar por equipo"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                  </svg>
                  Equipos
                  {filterMachines.length > 0 && (
                    <span className="bg-white text-blue-600 text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                      {filterMachines.length}
                    </span>
                  )}
                </button>

                {showMachineFilter && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-30 w-72 max-h-80 overflow-y-auto">
                    <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                      <span className="font-semibold text-gray-700 text-sm">Filtrar equipos</span>
                      <div className="flex gap-2">
                        {filterMachines.length > 0 && (
                          <button
                            onClick={() => setFilterMachines([])}
                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                          >
                            Limpiar
                          </button>
                        )}
                        <button
                          onClick={() => setShowMachineFilter(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div className="p-2">
                      {machines.map(machineName => (
                        <label key={machineName} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterMachines.includes(machineName)}
                            onChange={() => toggleMachineFilter(machineName)}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{machineName}</span>
                          {machineCodeMap[machineName] && (
                            <span className="text-xs text-gray-400 font-mono ml-auto">{machineCodeMap[machineName]}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Botón IA — transversal a ambas vistas */}
              <button
                onClick={handleOpenAI}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg active:scale-95 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Optimizar con IA
              </button>

              {viewMode === 'kanban' && (
                <button
                  onClick={() => setMachineSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
                  title={`Ordenar máquinas por código ${machineSortDir === 'asc' ? 'descendente' : 'ascendente'}`}
                >
                  {machineSortDir === 'asc'
                    ? <ChevronUp className="w-4 h-4 text-blue-600" />
                    : <ChevronDown className="w-4 h-4 text-blue-600" />}
                  Ordenar
                </button>
              )}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                  isFetching
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                }`}
                title="Actualizar tablero"
              >
                <svg
                  className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isFetching ? '...' : 'Actualizar'}
              </button>
            </div>
          </div>
          
          {/* Indicadores Clave */}
          <div className="flex gap-4">
            <div className="flex-1 bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">Backlog</p>
                  <p className="text-xs text-red-600 mt-0.5">Pendientes de planear</p>
                </div>
                <div className="text-3xl font-bold text-red-600">{backlogCount}</div>
              </div>
            </div>
            
            <div className="flex-1 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Semana</p>
                  <p className="text-xs text-blue-600 mt-0.5">Planeadas próximos 7 días</p>
                </div>
                <div className="text-3xl font-bold text-blue-600">{weekCount}</div>
              </div>
            </div>
            
            <div className="flex-1 bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700">Día</p>
                  <p className="text-xs text-green-600 mt-0.5">Planeadas para hoy</p>
                </div>
                <div className="text-3xl font-bold text-green-600">{todayCount}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Renderizado condicional: Gantt o Kanban */}
        {viewMode === 'gantt' ? (
          <div className="flex-1 overflow-auto p-4">
            <GanttView
              onDataLoaded={setGanttCounters}
              filterMachines={filterMachines}
              onOptimizationApplied={() => refetch()}
            />
          </div>
        ) : (
        <div className="flex-1 overflow-auto">
          <div className="min-w-max p-4">
            {/* Headers */}
            <div className="flex mb-2 sticky top-0 bg-gray-50 z-10 pb-2">
              <div className="w-48 flex-shrink-0"></div>
              {columnHeaders.map((col) => (
                <div key={col.id} className="flex-1 min-w-[280px] px-2">
                  <div className="bg-white border border-gray-300 rounded-lg p-2 text-center">
                    <h3 className="font-bold text-gray-800">{col.label}</h3>
                    <p className="text-xs text-gray-500">{col.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Swimlanes */}
            {groupedOrders.map((swimlane, idx) => (
              <div key={idx} className="flex mb-4 border-t border-gray-300 pt-2">
                {/* Machine Label */}
                <div className="w-48 flex-shrink-0 pr-4">
                  <button
                    onClick={() => handleMachineClick(swimlane.machineName)}
                    className={`sticky left-0 w-full rounded-lg p-3 transition-all duration-200 ${
                      selectedMachine === swimlane.machineName
                        ? 'bg-blue-600 border-2 border-blue-700 shadow-lg scale-105'
                        : 'bg-blue-100 border border-blue-300 hover:bg-blue-200 hover:shadow-md'
                    }`}
                  >
                    <h4 className={`font-semibold text-sm leading-tight ${
                      selectedMachine === swimlane.machineName
                        ? 'text-white'
                        : 'text-blue-900'
                    }`}>
                      {swimlane.machineName || 'SIN ASIGNAR'}
                    </h4>
                    {swimlane.machineName && machineCodeMap[swimlane.machineName] && (
                      <p className={`text-xs mt-0.5 font-mono ${
                        selectedMachine === swimlane.machineName
                          ? 'text-blue-200'
                          : 'text-blue-500'
                      }`}>
                        {machineCodeMap[swimlane.machineName]}
                      </p>
                    )}
                    {selectedMachine === swimlane.machineName && (
                      <p className="text-xs text-blue-100 mt-1">Clic para mostrar todo</p>
                    )}
                  </button>
                </div>

                {/* Columns */}
                {swimlane.columns.map((col) => {
                  const isDropDisabled = col.column === 'IN_PRODUCTION';

                  const dropId = `${col.column}-${
                    swimlane.machineName?.replace(/ /g, '_') || 'SIN_ASIGNAR'
                  }`;

                  return (
                    <div key={dropId} className="flex-1 min-w-[280px] px-2">
                      <Droppable droppableId={dropId} isDropDisabled={isDropDisabled}>
                        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[120px] rounded-lg p-2 ${
                              isDropDisabled
                                ? 'bg-gray-200 border-2 border-dashed border-gray-400'
                                : snapshot.isDraggingOver
                                ? 'bg-blue-100 border-2 border-solid border-blue-400'
                                : 'bg-gray-100 border-2 border-dashed border-gray-300'
                            }`}
                          >
                            {col.orders.map((order, index) => (
                              <Draggable key={order.id} draggableId={order.id} index={index}>
                                {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={snapshot.isDragging ? 'opacity-50' : ''}
                                    style={{
                                      ...provided.draggableProps.style,
                                      cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                    }}
                                  >
                                    <OrderCard
                                      order={order}
                                      onSchedule={handleScheduleClick}
                                      onViewMaterials={handleViewMaterialsClick}
                                      weightProgress={weightProgressMap[String(order.id)]}
                                      timeAlert={timeAlertMap[String(order.id)] ?? null}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder as React.ReactNode}
                            {!isDropDisabled && col.orders.length === 0 && (
                              <div className="text-center text-gray-400 text-sm py-4">
                                Suelta aquí
                              </div>
                            )}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* ======================== MODAL IA 3 PASOS ======================== */}
      {aiModalStep > 0 && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

            {/* ——— PASO 1: Contexto ——— */}
            {aiModalStep === 1 && (
              <>
                <div className="p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Configurar Optimización con IA
                      </h3>
                      <p className="text-purple-100 text-sm mt-1">Paso 1 de 2 — Define el contexto para que Gemini optimice tu plan</p>
                    </div>
                    <button onClick={closeAiModal} className="text-white/70 hover:text-white text-2xl leading-none">×</button>
                  </div>
                </div>

                <AiContextForm
                  initial={aiContext}
                  error={aiError}
                  onCancel={closeAiModal}
                  onSubmit={handleContinueAI}
                />
              </>
            )}

            {/* ——— PASO 2: Procesando ——— */}
            {aiModalStep === 2 && (
              <div className="p-12 text-center">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-spin opacity-20"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Gemini está analizando tu plan...</h3>
                <p className="text-gray-500 text-sm">El agente está procesando las órdenes, máquinas y reglas para generar la optimización ideal.</p>
              </div>
            )}

            {/* ——— PASO 3: Preview ——— */}
            {aiModalStep === 3 && aiOptimization && (
              <>
                <div className="p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold">Vista Previa del Plan Optimizado</h3>
                      <p className="text-purple-100 text-sm mt-1">Paso 2 de 2 — Revisa y elige cómo proceder</p>
                    </div>
                    <button onClick={closeAiModal} className="text-white/70 hover:text-white text-2xl leading-none">×</button>
                  </div>
                </div>

                <div className="p-6">
                  {aiSimulationId != null && (
                    <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800">
                      ✅ Simulación #{aiSimulationId} guardada automáticamente —{' '}
                      <Link to="/app/planning-simulations" className="underline font-medium">ver en Simulaciones de Planeación</Link>
                    </div>
                  )}
                  {aiOptimization.analysis.data_quality && aiOptimization.analysis.data_quality.confidence !== 'HIGH' && (
                    <div className={`mb-3 rounded-lg border px-3 py-2 text-sm ${aiOptimization.analysis.data_quality.confidence === 'LOW' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                      {aiOptimization.analysis.data_quality.confidence === 'LOW' ? 'Confianza baja' : 'Confianza media'}:{' '}
                      {aiOptimization.analysis.data_quality.fallbackOrders} de {aiOptimization.analysis.data_quality.orders} órdenes con duración de respaldo
                      y {aiOptimization.analysis.data_quality.assumedOrders} con valores asumidos. Revisa las advertencias.
                    </div>
                  )}
                  {/* Métricas */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {aiOptimization.analysis.total_setup_time_saved_minutes || 0}
                      </div>
                      <div className="text-xs text-green-700 mt-1">min. setup ahorrados</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {aiOptimization.analysis.workload_balance_score == null ? '—' : `${(aiOptimization.analysis.workload_balance_score * 100).toFixed(0)}%`}
                      </div>
                      <div className="text-xs text-blue-700 mt-1">balance de carga</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {aiOptimization.analysis.on_time_delivery_rate == null ? '—' : `${(aiOptimization.analysis.on_time_delivery_rate * 100).toFixed(0)}%`}
                      </div>
                      <div className="text-xs text-purple-700 mt-1">entregas a tiempo</div>
                    </div>
                  </div>

                  {/* Recomendaciones */}
                  {aiOptimization.recommendations?.length > 0 && (
                    <div className="mb-5">
                      <h4 className="font-semibold text-gray-700 mb-2 text-sm">✅ Recomendaciones</h4>
                      <ul className="space-y-1.5">
                        {aiOptimization.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="text-green-500 flex-shrink-0 mt-0.5">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Advertencias */}
                  {aiOptimization.warnings?.length > 0 && (
                    <div className="mb-5">
                      <h4 className="font-semibold text-orange-600 mb-2 text-sm">⚠️ Advertencias</h4>
                      <ul className="space-y-1.5">
                        {aiOptimization.warnings.map((w, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-orange-600">
                            <span className="flex-shrink-0 mt-0.5">•</span>
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tabla de programación */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-2 text-sm">
                      📅 Programación sugerida ({aiOptimization.optimized_schedule?.length || 0} órdenes)
                    </h4>
                    <div className="border rounded-xl overflow-hidden max-h-52 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-gray-600 font-semibold">#</th>
                            <th className="px-3 py-2 text-left text-gray-600 font-semibold">Orden</th>
                            <th className="px-3 py-2 text-left text-gray-600 font-semibold">Máquina</th>
                            <th className="px-3 py-2 text-left text-gray-600 font-semibold">Inicio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiOptimization.optimized_schedule?.map((item, idx) => (
                            <tr key={idx} className="border-t hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-400">{item.sequence}</td>
                              <td className="px-3 py-2 font-medium text-gray-800">{item.order_id}</td>
                              <td className="px-3 py-2">
                                <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full" title={item.assigned_machine}>
                                  {machineCodeToNameMap[item.assigned_machine] || item.assigned_machine}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-600">{item.recommended_start?.split(' ')[0] || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* 3 opciones */}
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setAiModalStep(1)}
                      className="flex flex-col items-center gap-1.5 px-3 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-200 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className="text-center leading-tight">Ajustar contexto</span>
                    </button>
                    <button
                      onClick={() => { closeAiModal(); setViewMode('kanban'); }}
                      className="flex flex-col items-center gap-1.5 px-3 py-3 bg-amber-100 text-amber-700 rounded-xl font-medium text-sm hover:bg-amber-200 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="text-center leading-tight">Editar órdenes</span>
                    </button>
                    <button
                      onClick={handleApplyOptimization}
                      disabled={aiApplying}
                      className="flex flex-col items-center gap-1.5 px-3 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-sm hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all"
                    >
                      {aiApplying ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className="text-center leading-tight">{aiApplying ? 'Ejecutando...' : 'Ejecutar plan'}</span>
                    </button>
                  </div>

                  {aiError && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                      {aiError}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de selección de máquina */}
      <MachineSelectionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPendingDragOperation(null);
          setEditingOrder(null);
        }}
        onSelect={handleMachineSelect}
        initialMachine={editingOrder?.machineName || pendingDragOperation?.machineName || undefined}
        initialDate={editingOrder?.plannedStartDate || undefined}
        initialNotes={(() => {
          // Extract observations from notes JSON if it exists
          if (editingOrder?.notes) {
            try {
              const parsed = JSON.parse(editingOrder.notes);
              return parsed.observations || '';
            } catch {
              // If not valid JSON, return as-is (legacy plain text)
              return editingOrder.notes;
            }
          }
          return undefined;
        })()}
      />

      <MaterialAvailabilityModal
        isOpen={!!materialViewer}
        mode={materialViewer?.mode ?? 'readonly'}
        orderLabel={materialViewer?.order.orderNumber ?? ''}
        isLoading={!!materialViewer?.loading}
        result={materialViewer?.result ?? null}
        isSubmitting={syncDespachado.isPending || updateOrderMutation.isPending}
        onClose={() => { setMaterialViewer(null); setPendingMove(null); setOverrideNote(''); }}
        onConfirm={handleConfirmMove}
        note={overrideNote}
        onNoteChange={setOverrideNote}
      />
    </DragDropContext>
  );
};

export default PlanningBoardView;
